import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrowLongRightIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import {
  GET_VENDOR_OPTIONS,
  GET_MERGE_PREVIEW,
  GET_FINANCIAL_ROLLUP,
} from "./queries";

const client = createApolloClient();

// The pair that spawned this tool: Those Little Donuts → Those Little Donuts Inc.
const DEFAULT_SOURCE_ID = 2682;
const DEFAULT_TARGET_ID = 3416;

// How many item lines to render per group before collapsing to "+ N more".
const MAX_ITEM_LINES = 10;

// ===== Query result types =====
type VendorOption = {
  id: number;
  name: string;
  is_active: boolean;
  organization: { id: number; name: string } | null;
};

type VendorRecord = {
  id: number;
  name: string;
  type: string | null;
  other_type: string | null;
  poc_name: string | null;
  poc_email: string | null;
  poc_phone: string | null;
  revenue_share: number | null;
  flat_fee: number | null;
  sales_tax_responsible: boolean | null;
  is_active: boolean;
  created_at: string;
  organization: { id: number; name: string } | null;
};

type LocationData = {
  id: number;
  name: string;
  is_active: boolean;
  event: { id: number; name: string } | null;
  location_menus: Array<{
    menu: { id: number; name: string; is_active: boolean; vendor_id: number | null } | null;
  }>;
};

type MenuData = {
  id: number;
  name: string;
  is_active: boolean;
  event: { id: number; name: string } | null;
  menu_items_aggregate: { aggregate: { count: number } | null };
  location_menus: Array<{
    location: { id: number; name: string; vendor_id: number | null } | null;
  }>;
};

type ItemData = {
  id: number;
  name: string;
  price: number | null; // cents
  is_active: boolean;
  event: { id: number; name: string } | null;
  menu_items: Array<{ menu_id: number }>;
};

type CountAgg = { aggregate: { count: number } | null };
type OrdersAgg = {
  aggregate: {
    count: number;
    sum: { subtotal: number | null; tax: number | null; tip: number | null } | null;
  } | null;
};
type AmountAgg = {
  aggregate: { count: number; sum: { amount: number | null } | null } | null;
};

type MergePreviewData = {
  source: VendorRecord | null;
  target: VendorRecord | null;
  srcLocations: LocationData[];
  tgtLocations: LocationData[];
  srcMenus: MenuData[];
  tgtMenus: MenuData[];
  srcItems: ItemData[];
  tgtItems: ItemData[];
  srcUsers: CountAgg;
  tgtUsers: CountAgg;
};

type FinancialData = {
  srcOrders: OrdersAgg;
  tgtOrders: OrdersAgg;
  srcTransactions: AmountAgg;
  tgtTransactions: AmountAgg;
  srcPayments: AmountAgg;
  tgtPayments: AmountAgg;
};

// ===== Display row models =====
type OpKind = "location" | "menu" | "item" | "group";
type OpRow = { key: string; kind: OpKind; name: string; detail: string; note?: string };
type FinRow = { table: string; rows: number; amount: number };

// ===== Formatters =====
const fmtPercent = (fraction: number | null | undefined) =>
  fraction == null ? "—" : `${(fraction * 100).toFixed(0)}%`;
const fmtCents = (cents: number | null | undefined) =>
  cents == null
    ? "—"
    : `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
const fmtDate = (d: string) => {
  try {
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return d;
  }
};
const activeLabel = (b: boolean) => (b ? "Active" : "Inactive");

// Flatten one vendor's records into display rows, nesting by the real joins:
// location ▸ its menus (location_menu) ▸ their items (menu_item). Menus not
// attached to one of the vendor's own locations, and items not on any menu,
// surface in their own groups instead of being hidden. Cross-vendor links
// (e.g. a menu owned by someone else attached to this vendor's location) get
// an explicit note — those tangles are what this page exists to show. The
// note has to know which side it's on: a menu owned by a third vendor is
// untouched by this merge, but one owned by the OTHER side of this very merge
// is either arriving (target side) or already where it's going (source side).
const buildOpRows = (
  side: "source" | "target",
  vendorId: number,
  otherVendorId: number,
  locations: LocationData[],
  menus: MenuData[],
  items: ItemData[]
): OpRow[] => {
  const rows: OpRow[] = [];
  const menusById = new Map(menus.map((m) => [m.id, m]));
  const renderedMenuIds = new Set<number>();
  const renderedItemIds = new Set<number>();

  const pushItemsOfMenu = (menuId: number) => {
    const menuItems = items.filter(
      (it) =>
        !renderedItemIds.has(it.id) &&
        it.menu_items.some((mi) => mi.menu_id === menuId)
    );
    menuItems.slice(0, MAX_ITEM_LINES).forEach((it) => {
      renderedItemIds.add(it.id);
      rows.push({
        key: `item-${it.id}`,
        kind: "item",
        name: it.name,
        detail: `${fmtCents(it.price)} · ${activeLabel(it.is_active)}`,
      });
    });
    if (menuItems.length > MAX_ITEM_LINES) {
      menuItems.slice(MAX_ITEM_LINES).forEach((it) => renderedItemIds.add(it.id));
      rows.push({
        key: `item-more-${menuId}`,
        kind: "item",
        name: `+ ${menuItems.length - MAX_ITEM_LINES} more items`,
        detail: "",
      });
    }
  };

  const pushMenu = (menu: MenuData, note?: string) => {
    renderedMenuIds.add(menu.id);
    const itemCount = menu.menu_items_aggregate.aggregate?.count ?? 0;
    rows.push({
      key: `menu-${menu.id}`,
      kind: "menu",
      name: menu.name,
      detail: `${itemCount} linked item${itemCount === 1 ? "" : "s"} · ${activeLabel(menu.is_active)}`,
      note,
    });
    pushItemsOfMenu(menu.id);
  };

  // 1. Locations, each with the menus attached to it.
  locations.forEach((loc) => {
    rows.push({
      key: `loc-${loc.id}`,
      kind: "location",
      name: loc.name,
      detail: `${loc.event?.name ?? "no event"} · ${activeLabel(loc.is_active)}`,
    });
    loc.location_menus.forEach(({ menu }) => {
      if (!menu) return;
      const owned = menusById.get(menu.id);
      if (owned && !renderedMenuIds.has(menu.id)) {
        pushMenu(owned);
      } else if (!owned) {
        // Attached to this vendor's location but owned by someone else.
        const owner = menu.vendor_id;
        const note =
          owner === otherVendorId
            ? side === "target"
              ? `owned by source #${owner} — moves here with this merge`
              : `owned by target #${owner} — already where it's going`
            : `owned by vendor #${owner ?? "?"} — not part of this merge`;
        rows.push({
          key: `menu-foreign-${loc.id}-${menu.id}`,
          kind: "menu",
          name: menu.name,
          detail: activeLabel(menu.is_active),
          note,
        });
      }
    });
  });

  // 2. This vendor's menus that aren't on any of its own locations.
  menus
    .filter((m) => !renderedMenuIds.has(m.id))
    .forEach((menu) => {
      const link = menu.location_menus.find((lm) => lm.location)?.location;
      const note = !link
        ? "not attached to any location"
        : link.vendor_id === otherVendorId
        ? `attached to the ${side === "source" ? "target" : "source"}'s location "${link.name}" (#${link.id})`
        : `attached to "${link.name}" (#${link.id}, vendor #${link.vendor_id ?? "?"})`;
      pushMenu(menu, note);
    });

  // 3. This vendor's items that aren't on any menu rendered above.
  const orphanItems = items.filter((it) => !renderedItemIds.has(it.id));
  if (orphanItems.length > 0) {
    rows.push({
      key: "group-orphan-items",
      kind: "group",
      name: "Items not on any menu",
      detail: `${orphanItems.length} item${orphanItems.length === 1 ? "" : "s"}`,
    });
    orphanItems.slice(0, MAX_ITEM_LINES).forEach((it) => {
      rows.push({
        key: `item-${it.id}`,
        kind: "item",
        name: it.name,
        detail: `${fmtCents(it.price)} · ${it.event?.name ?? "no event"} · ${activeLabel(it.is_active)}`,
      });
    });
    if (orphanItems.length > MAX_ITEM_LINES) {
      rows.push({
        key: "item-more-orphan",
        kind: "item",
        name: `+ ${orphanItems.length - MAX_ITEM_LINES} more items`,
        detail: "",
      });
    }
  }

  return rows;
};

const buildFinRows = (fin: FinancialData, side: "src" | "tgt"): FinRow[] => {
  const orders = side === "src" ? fin.srcOrders : fin.tgtOrders;
  const payments = side === "src" ? fin.srcPayments : fin.tgtPayments;
  const txns = side === "src" ? fin.srcTransactions : fin.tgtTransactions;
  const oAgg = orders.aggregate;
  const orderTotal =
    (oAgg?.sum?.subtotal ?? 0) + (oAgg?.sum?.tax ?? 0) + (oAgg?.sum?.tip ?? 0);
  return [
    { table: "Orders", rows: oAgg?.count ?? 0, amount: orderTotal },
    {
      table: "Payments",
      rows: payments.aggregate?.count ?? 0,
      amount: payments.aggregate?.sum?.amount ?? 0,
    },
    {
      table: "Transactions",
      rows: txns.aggregate?.count ?? 0,
      amount: txns.aggregate?.sum?.amount ?? 0,
    },
  ];
};

// ===== Diff building blocks =====
const Marker = ({ kind }: { kind: "-" | "+" | " " }) => (
  <span
    className={`mr-1 inline-block w-3 font-mono font-bold ${
      kind === "-" ? "text-red-600" : kind === "+" ? "text-green-600" : "text-transparent"
    }`}
  >
    {kind}
  </span>
);

const KIND_STYLE: Record<OpKind, { indent: string; badge: string; label: string }> = {
  location: { indent: "", badge: "bg-blue-100 text-blue-800", label: "LOCATION" },
  menu: { indent: "pl-6", badge: "bg-purple-100 text-purple-800", label: "MENU" },
  item: { indent: "pl-12", badge: "bg-gray-100 text-gray-600", label: "ITEM" },
  group: { indent: "", badge: "bg-gray-200 text-gray-700", label: "ITEMS" },
};

// One half-row (2 cells) of the split operational table.
const OpCells = ({
  side,
  row,
  excluded,
  note,
}: {
  side: "removed" | "added" | "unchanged" | "empty";
  row?: OpRow;
  excluded?: boolean;
  note?: string;
}) => {
  if (!row || (side === "added" && excluded)) {
    return <td colSpan={2} className="bg-gray-50 px-3 py-1.5" />;
  }
  const bg =
    side === "removed" ? "bg-red-50" : side === "added" ? "bg-green-50" : "bg-white";
  const marker: "-" | "+" | " " =
    side === "removed" ? "-" : side === "added" ? "+" : " ";
  const k = KIND_STYLE[row.kind];
  if (excluded) {
    return (
      <>
        <td className={`bg-gray-50 px-3 py-1.5 ${k.indent}`}>
          <Marker kind=" " />
          <span className="text-gray-400 line-through">{row.name}</span>
          <span className="ml-2 text-gray-400">stays behind</span>
        </td>
        <td className="bg-gray-50 px-3 py-1.5" />
      </>
    );
  }
  return (
    <>
      <td className={`${bg} px-3 py-1.5 ${k.indent}`}>
        <Marker kind={marker} />
        {(row.kind === "menu" || row.kind === "item") && (
          <span className="mr-1.5 text-gray-300">└</span>
        )}
        <span className={`mr-1.5 rounded px-1 py-px text-[10px] font-semibold ${k.badge}`}>
          {k.label}
        </span>
        <span className="font-medium text-gray-900">{row.name}</span>
        {row.note && (
          <span className="ml-1.5 rounded bg-amber-100/80 px-1.5 py-px text-amber-800">
            {row.note}
          </span>
        )}
        {note && <span className="ml-1.5 text-gray-400">{note}</span>}
      </td>
      <td className={`${bg} px-3 py-1.5 text-right tabular-nums text-gray-600`}>
        {row.detail}
      </td>
    </>
  );
};

// One half-row (3 cells) of the split financial table.
const FinCells = ({
  side,
  row,
  excluded,
  note,
}: {
  side: "removed" | "added";
  row: FinRow;
  excluded: boolean;
  note?: string;
}) => {
  if (excluded) {
    if (side === "added") {
      return <td colSpan={3} className="bg-gray-50 px-3 py-2" />;
    }
    return (
      <>
        <td className="bg-gray-50 px-3 py-2">
          <Marker kind=" " />
          <span className="text-gray-400 line-through">{row.table}</span>
          <span className="ml-2 text-gray-400">stays behind</span>
        </td>
        <td className="bg-gray-50 px-3 py-2" />
        <td className="bg-gray-50 px-3 py-2" />
      </>
    );
  }
  const bg = side === "removed" ? "bg-red-50" : "bg-green-50";
  return (
    <>
      <td className={`${bg} px-3 py-2`}>
        <Marker kind={side === "removed" ? "-" : "+"} />
        <span className="font-medium text-gray-900">{row.table}</span>
        {note && <span className="ml-1.5 text-gray-400">{note}</span>}
      </td>
      <td className={`${bg} px-3 py-2 text-right tabular-nums text-gray-800`}>
        {row.rows.toLocaleString("en-US")} rows
      </td>
      <td className={`${bg} px-3 py-2 text-right tabular-nums text-gray-800`}>
        {fmtCents(row.amount)}
      </td>
    </>
  );
};

const ExcludeCheckbox = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) => (
  <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-gray-600">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="h-3.5 w-3.5 rounded border-gray-300 text-red-600 focus:ring-red-500"
    />
    {label}
  </label>
);

// Split-table column headers: source half + target half.
const SplitHead = ({
  cols,
  source,
  target,
}: {
  cols: string[];
  source: VendorRecord;
  target: VendorRecord;
}) => (
  <thead>
    <tr className="border-b border-gray-200 text-left">
      <th
        colSpan={cols.length}
        className="bg-red-100/60 px-3 py-2 font-semibold text-red-900"
      >
        #{source.id} — {source.name}
      </th>
      <th
        colSpan={cols.length}
        className="border-l-2 border-gray-300 bg-green-100/60 px-3 py-2 font-semibold text-green-900"
      >
        #{target.id} — {target.name} (after merge)
      </th>
    </tr>
    <tr className="border-b border-gray-200 text-left text-gray-500">
      {cols.map((c) => (
        <th
          key={`s-${c}`}
          className={`px-3 py-1.5 font-medium ${c === cols[0] ? "" : "text-right"}`}
        >
          {c}
        </th>
      ))}
      {cols.map((c, i) => (
        <th
          key={`t-${c}`}
          className={`px-3 py-1.5 font-medium ${
            i === 0 ? "border-l-2 border-gray-300" : "text-right"
          }`}
        >
          {c}
        </th>
      ))}
    </tr>
  </thead>
);

// ===== Vendor record card (source retires whole, target untouched) =====
const VendorCard = ({
  kind,
  vendor,
  users,
  header,
}: {
  kind: "removed" | "unchanged";
  vendor: VendorRecord;
  users: number;
  header: string;
}) => {
  const frame =
    kind === "removed" ? "bg-red-50 border-red-300" : "bg-white border-gray-200";
  const marker: "-" | " " = kind === "removed" ? "-" : " ";
  const poc = [vendor.poc_name, vendor.poc_email, vendor.poc_phone]
    .filter((s) => s && s.trim())
    .join(" · ");
  const fields = [
    { label: "Org", value: vendor.organization ? `#${vendor.organization.id} — ${vendor.organization.name}` : "—" },
    { label: "Type", value: vendor.other_type || vendor.type || "—" },
    { label: "Status", value: activeLabel(vendor.is_active) },
    { label: "POC", value: poc || "—" },
    { label: "Rev Share", value: fmtPercent(vendor.revenue_share) },
    { label: "Flat Fee", value: fmtCents(vendor.flat_fee) },
    {
      label: "Sales Tax",
      value: vendor.sales_tax_responsible ? "Responsible" : "Not responsible",
    },
    { label: "Clerks", value: users === 0 ? "none" : `${users} account${users === 1 ? "" : "s"} (moved optionally)` },
    { label: "Created", value: fmtDate(vendor.created_at) },
  ];
  return (
    <div className={`rounded-lg border ${frame} px-3 py-2`}>
      <div className="flex items-center gap-1">
        <Marker kind={marker} />
        <span className="flex-1 text-sm font-semibold text-gray-900">
          #{vendor.id} — {vendor.name}
          <span className="ml-2 text-xs font-normal text-gray-500">{header}</span>
        </span>
      </div>
      <div className="pt-0.5">
        {fields.map((f) => (
          <div key={f.label} className="flex items-center gap-1">
            <Marker kind={marker} />
            <span className="w-20 shrink-0 text-xs font-medium text-gray-500">
              {f.label}
            </span>
            <span className="text-xs text-gray-800">{f.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const VendorSelect = ({
  label,
  color,
  value,
  options,
  onChange,
}: {
  label: string;
  color: "red" | "green";
  value: number;
  options: VendorOption[];
  onChange: (id: number) => void;
}) => (
  <div>
    <label
      className={`mb-1 block text-xs font-semibold uppercase tracking-wide ${
        color === "red" ? "text-red-700" : "text-green-700"
      }`}
    >
      {label}
    </label>
    <select
      className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 ${
        color === "red" ? "border-red-300 bg-red-50" : "border-green-300 bg-green-50"
      }`}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
    >
      {options.map((v) => (
        <option key={v.id} value={v.id}>
          #{v.id} — {v.name}
          {v.organization ? ` (${v.organization.name})` : ""}
          {v.is_active ? "" : " [inactive]"}
        </option>
      ))}
    </select>
  </div>
);

// ===== Page =====
export const MergeVendorPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceId = Number(searchParams.get("source")) || DEFAULT_SOURCE_ID;
  const targetId = Number(searchParams.get("target")) || DEFAULT_TARGET_ID;

  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [preview, setPreview] = useState<MergePreviewData | null>(null);
  const [financial, setFinancial] = useState<FinancialData | null>(null);
  const [loading, setLoading] = useState(false);
  const [finLoading, setFinLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [exclude, setExclude] = useState({
    locations: false,
    menus: false,
    items: false,
    financial: false,
  });
  const toggle = (key: keyof typeof exclude) =>
    setExclude((prev) => ({ ...prev, [key]: !prev[key] }));
  const kindExcluded: Record<OpKind, boolean> = {
    location: exclude.locations,
    menu: exclude.menus,
    item: exclude.items,
    group: exclude.items,
  };

  const setVendorParam = (key: "source" | "target", id: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("source", key === "source" ? String(id) : String(sourceId));
    next.set("target", key === "target" ? String(id) : String(targetId));
    setSearchParams(next, { replace: true });
  };

  // Vendor picker options, once.
  useEffect(() => {
    client
      .query<{ vendors: VendorOption[] }>({ query: GET_VENDOR_OPTIONS })
      .then(({ data }) => setVendorOptions(data.vendors))
      .catch((e: any) => setError(e?.message ?? "Failed to load vendors"));
  }, []);

  // Preview (vendors + operational tree), then financial rollups keyed off
  // the location ids the preview returned.
  useEffect(() => {
    if (!sourceId || !targetId || sourceId === targetId) {
      setPreview(null);
      setFinancial(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setFinancial(null);
      try {
        const { data } = await client.query<MergePreviewData>({
          query: GET_MERGE_PREVIEW,
          variables: { src: sourceId, tgt: targetId },
          fetchPolicy: "network-only",
        });
        if (cancelled) return;
        setPreview(data);
        setFinLoading(true);
        try {
          const { data: fin } = await client.query<FinancialData>({
            query: GET_FINANCIAL_ROLLUP,
            variables: {
              srcLocIds: data.srcLocations.map((l) => l.id),
              tgtLocIds: data.tgtLocations.map((l) => l.id),
            },
            fetchPolicy: "network-only",
          });
          if (!cancelled) setFinancial(fin);
        } finally {
          if (!cancelled) setFinLoading(false);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? "Failed to load merge preview");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [sourceId, targetId]);

  const srcOpRows = useMemo(
    () =>
      preview
        ? buildOpRows("source", sourceId, targetId, preview.srcLocations, preview.srcMenus, preview.srcItems)
        : [],
    [preview, sourceId, targetId]
  );
  const tgtOpRows = useMemo(
    () =>
      preview
        ? buildOpRows("target", targetId, sourceId, preview.tgtLocations, preview.tgtMenus, preview.tgtItems)
        : [],
    [preview, sourceId, targetId]
  );

  const source = preview?.source ?? null;
  const target = preview?.target ?? null;

  return (
    <div className="min-h-screen bg-gray-100 p-4 pb-28">
      {/* Preview banner */}
      <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800">
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
        <span>
          <span className="font-semibold">Preview only.</span> This page reads
          live production data but the merge itself is not wired up — nothing is
          written.
        </span>
      </div>

      {/* Header: vendor pickers + exclusions */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h1 className="mb-4 text-xl font-bold text-gray-900">Merge Vendor</h1>
        <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-4">
          <VendorSelect
            label="Source — merged & retired"
            color="red"
            value={sourceId}
            options={vendorOptions}
            onChange={(id) => setVendorParam("source", id)}
          />
          <ArrowLongRightIcon className="mb-2 h-6 w-6 text-gray-400" />
          <VendorSelect
            label="Target — receives everything"
            color="green"
            value={targetId}
            options={vendorOptions}
            onChange={(id) => setVendorParam("target", id)}
          />
        </div>
        <div className="mt-4 flex items-center gap-6 border-t border-gray-100 pt-4">
          <ExcludeCheckbox
            label="Exclude locations"
            checked={exclude.locations}
            onChange={() => toggle("locations")}
          />
          <ExcludeCheckbox
            label="Exclude menus"
            checked={exclude.menus}
            onChange={() => toggle("menus")}
          />
          <ExcludeCheckbox
            label="Exclude items"
            checked={exclude.items}
            onChange={() => toggle("items")}
          />
          <span className="h-4 w-px bg-gray-200" />
          <ExcludeCheckbox
            label="Exclude financial data"
            checked={exclude.financial}
            onChange={() => toggle("financial")}
          />
        </div>
      </div>

      {sourceId === targetId && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          Source and target are the same vendor — pick two different vendors.
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}
      {loading && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">
          Loading merge preview…
        </div>
      )}

      {source && target && !loading && (
        <>
          {/* Vendor records: source retires whole, target untouched */}
          <section className="mb-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <header className="border-b border-gray-200 bg-gray-50 px-4 py-2.5">
              <h3 className="text-sm font-semibold text-gray-900">
                Vendor Records{" "}
                <span className="ml-1 text-xs font-normal text-gray-500">
                  the source record is retired whole — the target record is not
                  modified
                </span>
              </h3>
            </header>
            <div className="grid grid-cols-2 gap-3 p-3">
              <VendorCard
                kind="removed"
                vendor={source}
                users={preview?.srcUsers.aggregate?.count ?? 0}
                header="retired after merge"
              />
              <VendorCard
                kind="unchanged"
                vendor={target}
                users={preview?.tgtUsers.aggregate?.count ?? 0}
                header="unchanged"
              />
            </div>
          </section>

          {/* Operational: one table, joins shown as location ▸ menu ▸ item nesting */}
          <section className="mb-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <header className="border-b border-gray-200 bg-gray-50 px-4 py-2.5">
              <h3 className="text-sm font-semibold text-gray-900">
                Operational{" "}
                <span className="ml-1 text-xs font-normal text-gray-500">
                  locations ▸ menus ▸ items, nested the way they join
                </span>
              </h3>
            </header>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <SplitHead cols={["Record", "Detail"]} source={source} target={target} />
                <tbody className="divide-y divide-gray-100">
                  {srcOpRows.map((row) => (
                    <tr
                      key={`s-${row.key}`}
                      className="[&>td:nth-child(3)]:border-l-2 [&>td:nth-child(3)]:border-gray-300"
                    >
                      <OpCells side="removed" row={row} excluded={kindExcluded[row.kind]} />
                      <OpCells side="added" row={row} excluded={kindExcluded[row.kind]} note="incoming" />
                    </tr>
                  ))}
                  {tgtOpRows.map((row) => (
                    <tr
                      key={`t-${row.key}`}
                      className="[&>td:nth-child(2)]:border-l-2 [&>td:nth-child(2)]:border-gray-300"
                    >
                      <OpCells side="empty" />
                      <OpCells side="unchanged" row={row} note="already on target" />
                    </tr>
                  ))}
                  {srcOpRows.length === 0 && tgtOpRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-4 text-center text-gray-400">
                        Neither vendor owns any locations, menus, or items.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Financial: one table, rows reassigned wholesale */}
          <section className="mb-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <header className="border-b border-gray-200 bg-gray-50 px-4 py-2.5">
              <h3 className="text-sm font-semibold text-gray-900">
                Financial{" "}
                <span className="ml-1 text-xs font-normal text-gray-500">
                  rows keep their IDs and statuses, only vendor_id changes —
                  counted via each vendor&apos;s locations (orders by vendor_id
                  is unindexed); orders total = subtotal + tax + tip
                </span>
              </h3>
            </header>
            {finLoading && (
              <div className="px-4 py-3 text-xs text-gray-500">
                Counting orders, payments, and transactions…
              </div>
            )}
            {financial && !finLoading && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <SplitHead cols={["Table", "Rows", "Amount"]} source={source} target={target} />
                  <tbody className="divide-y divide-gray-100">
                    {buildFinRows(financial, "src").map((row, i) => (
                      <tr
                        key={row.table}
                        className="[&>td:nth-child(4)]:border-l-2 [&>td:nth-child(4)]:border-gray-300"
                      >
                        <FinCells side="removed" row={row} excluded={exclude.financial} />
                        <FinCells
                          side="added"
                          row={{
                            ...row,
                            rows: row.rows + buildFinRows(financial, "tgt")[i].rows,
                            amount: row.amount + buildFinRows(financial, "tgt")[i].amount,
                          }}
                          excluded={exclude.financial}
                          note={`reassigned to #${target.id}`}
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Sticky action bar */}
          <div className="fixed inset-x-0 bottom-0 border-t border-gray-200 bg-white/95 px-6 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.06)] backdrop-blur">
            <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
              <p className="text-sm text-gray-600">
                Everything not excluded moves to vendor{" "}
                <span className="font-mono">#{target.id}</span>, then vendor{" "}
                <span className="font-mono">#{source.id}</span> is deactivated.
              </p>
              <div className="flex shrink-0 items-center gap-2">
                <button className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50">
                  Preview merge
                </button>
                <button
                  disabled
                  title="Merge execution is not wired up yet"
                  className="cursor-not-allowed rounded-lg bg-gray-300 px-4 py-2 text-sm font-semibold text-white shadow-sm"
                >
                  Merge vendor
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
